import React, {MouseEvent, useId, useRef} from "react";
import {observer} from "mobx-react-lite";
import {useAppState} from "@stores/AppStateContext";

import {DAY_FORMAT, getShortMonthName} from "../../../../utils/date";
import {openCalendarSelectOptionsCtxMenu} from "./openCalendarSelectOptionsCtxMenu";
import {CalendarSelectInput} from "./CalendarSelectInput";

import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";

import "./CalendarSelect.css";

export interface DateSelectProps {
    selectedDate?: Date;
    onChange: (date: Date) => void;
    placeholder: string;
    mode?: "day" | "month" | "year";
    withBackground?: boolean;
    className?: string;
    tooltipTitle?: string;
}

export const DateSelect = observer((props: DateSelectProps) => {
    const appState = useAppState();
    const {
        selectedDate,
        onChange,
        placeholder,
        mode = "month",
        withBackground,
        tooltipTitle,
        className,
    } = props;
    const menuId = useId();
    const mainRef = useRef<HTMLDivElement>(null);
    const isOpen = appState.isCtxMenuOpen('calendar-select-options', menuId);
    const isMobile = screenSizeProvider.view !== 'desktop'

    const openOrUpdateCtxMenu = () => {
        openCalendarSelectOptionsCtxMenu(
            appState,
            mainRef.current!,
            {selectedDate, onChange, mode},
            {VERTICAL_SHIFT: 8, id: menuId, menuAlignment: isMobile ? 'center' : 'start'},
        );
    };

    const _onClick = (event: MouseEvent) => {
        event.stopPropagation();
        if (isOpen) {
            appState.closeContextMenusByType('calendar-select-options');
        } else {
            openOrUpdateCtxMenu()
        }
    };

    const label = selectedDate
        ? mode === "day"
            ? DAY_FORMAT(selectedDate)
            : mode === "year"
                ? selectedDate.getFullYear().toString()
                : `${getShortMonthName(selectedDate)} ${selectedDate.getFullYear()}`
        : placeholder;

    return (
        <CalendarSelectInput
            tooltipTitle={tooltipTitle}
            ref={mainRef}
            className={className}
            onClick={_onClick}
            label={label}
            withBackground={withBackground}
            isPlaceholder={label === placeholder}
            isOpen={isOpen}
        />
    );
});
